import React, { useCallback, useEffect, useState } from 'react';
import { formatAppDateTime } from '../../services/appConfigRuntime';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as FailedIcon,
  PlayArrow as RunningIcon,
  Refresh as RefreshIcon,
  Schedule as IdleIcon,
} from '@mui/icons-material';
import MetricsWidget from './MetricsWidget';

/**
 * Hub Flow dashboard panel.
 * Source: tenant sync_flow_instance (sfi_group=HubFlow) + datahub_api_log counts.
 */
const HubFlowSummaryPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantId, setTenantId] = useState(localStorage.getItem('tenantId'));

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const tid = localStorage.getItem('tenantId');
      setTenantId(tid);
      const url =
        tid && tid !== 'ALL' && tid !== 'null'
          ? `${process.env.REACT_APP_BACKEND_URL}synctrack/dashboard/hub-flows/summary/tenant/${tid}`
          : `${process.env.REACT_APP_BACKEND_URL}synctrack/dashboard/hub-flows/summary`;

      const res = await fetch(url, { headers });
      const json = await res.json();
      if (!res.ok || json.status === 'error') {
        throw new Error(json.error || `Failed to load Hub Flow summary (${res.status})`);
      }
      setData(json);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load Hub Flow summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const onTenant = () => fetchSummary();
    window.addEventListener('tenantChange', onTenant);
    const timer = setInterval(fetchSummary, 30000);
    return () => {
      window.removeEventListener('tenantChange', onTenant);
      clearInterval(timer);
    };
  }, [fetchSummary]);

  const flows = Array.isArray(data?.flows) ? data.flows : [];
  const formatTime = (v) => (v ? formatAppDateTime(v) : '—');

  const statusChip = (status, active) => {
    const s = (status || '').toUpperCase();
    if (active || s === 'RUNNING') {
      return <Chip size="small" color="info" icon={<RunningIcon />} label={status || 'Running'} />;
    }
    if (s === 'ERROR' || s === 'FAILED' || s === 'FAILURE') {
      return <Chip size="small" color="error" icon={<FailedIcon />} label={status} />;
    }
    if (s === 'COMPLETED' || s === 'SUCCESS') {
      return <Chip size="small" color="success" icon={<SuccessIcon />} label={status} />;
    }
    if (s === 'NOT STARTED' || s.includes('NOT')) {
      return <Chip size="small" color="default" icon={<IdleIcon />} label={status} />;
    }
    return <Chip size="small" label={status || '—'} />;
  };

  const scopeLabel =
    tenantId && tenantId !== 'ALL' ? `tenant ${tenantId}` : 'all tenants';

  return (
    <Box sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Hub Flow activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From Hub Flow instances ({data?.dataSource || 'sync_flow_instance + datahub_api_log'}) · {scopeLabel}
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={fetchSummary} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <MetricsWidget
            title="Active Hub Flows"
            value={data?.activeHubFlows ?? 0}
            loading={loading}
            icon="running"
            description="sfi_status = Running"
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricsWidget
            title="Successful records"
            value={data?.recordsSuccessfulTotal ?? 0}
            loading={loading}
            icon="completed"
            description="Sum of datahub_api_log.records_successful"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricsWidget
            title="Failed records"
            value={data?.recordsFailedTotal ?? 0}
            loading={loading}
            icon="failed"
            description="Sum of datahub_api_log.records_failed"
            color="error"
          />
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 480 }}>
          {loading && !data ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Flow</TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sub-category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>Failure reason</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Typography color="text.secondary" align="center" py={2}>
                        No Hub Flow instances found for this tenant.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  flows.map((row) => (
                    <TableRow key={row.flowInstanceId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.tenantName || (row.tenantId != null ? `Tenant ${row.tenantId}` : '—')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {row.description || row.flowName || 'Hub Flow'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.flowInstanceId}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.application || '—'}</TableCell>
                      <TableCell>{row.category || '—'}</TableCell>
                      <TableCell>{row.subCategory || '—'}</TableCell>
                      <TableCell>{statusChip(row.instanceStatus || row.runStatus, row.active)}</TableCell>
                      <TableCell align="right">{row.recordsSuccessful ?? 0}</TableCell>
                      <TableCell align="right">{row.recordsFailed ?? 0}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        {row.failureReason ? (
                          <Tooltip title={row.failureReason}>
                            <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 180, cursor: 'help' }}>
                              {row.failureReason}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatTime(row.startTime || row.lastExecutionTime)}</TableCell>
                      <TableCell>{formatTime(row.endTime)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default HubFlowSummaryPanel;
